import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { LocalStorageService } from 'ngx-webstorage';
import { AuthService, BreakpointService } from '@services';
import {
  DoubleDateTableFilter,
  getError,
  objectIsEmpty,
  TableChip,
  TableFilterConfig,
  TableFilter, TableFilterType,
} from '@shared';

@Component({
  selector: 'app-table-hot-filters',
  templateUrl: './table-hot-filters.component.html',
  styleUrls: ['./table-hot-filters.component.scss'],
})
export class TableHotFiltersComponent {
  protected readonly TableFilterType = TableFilterType;
  readonly getError = getError;
  private _config: TableFilterConfig;

  @Input()
  set config(value: TableFilterConfig) {
    if (value) {
      this._config = {
        ...value,
        filterGroups: value.filterGroups.map(group =>
          ({ ...group, filters: group.filters.filter(filter => !!filter.hotFilter) }),
        ),
      };
      this.initForm();
    }
  }

  get config(): TableFilterConfig {
    return this._config;
  }

  initialForm: object;
  form: UntypedFormGroup;
  @Output() filtersApplied: EventEmitter<Record<string, any>> = new EventEmitter<Record<string, any>>();
  @Output() showFilters: EventEmitter<void> = new EventEmitter<void>();
  private _filtersFromStorage: Record<string, any> = {};
  isMobile$ = this.breakpointService.isMobile$;

  constructor(
    private fb: UntypedFormBuilder,
    private storage: LocalStorageService,
    private authService: AuthService,
    private breakpointService: BreakpointService,
  ) {
  }

  private _chips: TableChip[] = [];

  get chips(): TableChip[] {
    return this._chips;
  }

  @Input() set chips(chips: TableChip[]) {
    this._chips = chips;
    this.initForm();
  }

  get filters(): Record<string, any> {
    return this._filtersFromStorage;
  }

  set filters(filters: Record<string, any>) {
    this._filtersFromStorage = filters;
  }

  private get storageKey(): string {
    return `${this.authService.user.userLogin}_${this.config.storageKey}`;
  }

  sortHotFilters(filters: TableFilter[]): TableFilter[] {
    return filters.sort((a, b) => a.hotFilter.order - b.hotFilter.order);
  }

  saveInitialForm(): void {
    this.initialForm = this.form?.getRawValue();
  }

  getGroup(groupName: string): UntypedFormGroup {
    return this.form.get(groupName) as UntypedFormGroup;
  }

  mergeObjects<T extends Record<string, any>, U extends Record<string, any>>(a: T, b: U): T & U {
    if (objectIsEmpty(a)) {
      return b as T & U;
    }

    const result: Record<string, any> = { ...a };

    for (const key in b) {
      if (b.hasOwnProperty(key)) {
        if (typeof b[key] === 'object' && b[key] !== null && !Array.isArray(b[key])) {
          result[key] = {
            ...a[key],
            ...b[key],
          };
        } else {
          result[key] = b[key];
        }
      }
    }

    return result as T & U;
  }

  applyFilterForm(): void {
    const hotFilters = this.form.getRawValue();
    const filters = this.retrieveFiltersFromStorage();
    const allFilters = this.mergeObjects(filters, hotFilters);

    this.saveFiltersToStorage(allFilters);
    this.filtersApplied.emit(allFilters);
  }

  openFilters(): void {
    this.showFilters.emit();
  }

  saveFiltersToStorage(filters: any): void {
    this.storage.store(this.storageKey, objectIsEmpty(filters) ? {} : filters);
    this.retrieveFiltersFromStorage();
  }

  clearField(field: AbstractControl, event?: Event, value?: any): void {
    if (event) {
      event.stopPropagation();
    }
    if (field.reset) {
      if (value === '' || value === null) {
        field.reset(value);

        return;
      }
      field.reset();
    }
  }

  private patchForm(): void {
    this.form.patchValue(this.retrieveFiltersFromStorage());
  }

  private retrieveFiltersFromStorage(): Record<string, any> {
    this.filters = this.storage.retrieve(this.storageKey) ?? {};

    return this.filters;
  }

  private initForm(): void {
    if (!this.config) {
      return;
    }
    this.form = this.fb.group(
      this.config.filterGroups.reduce((acc, group) => {
        if (!group.controlName) {
          return;
        }
        acc[group.controlName] = this.fb.group(
          group.filters.reduce((acc, filter) => {
            if (filter.type === TableFilterType.DoubleDate) {
              const ddFilter = filter as DoubleDateTableFilter;
              acc[ddFilter.controlName] = this.fb.group(
                {
                  [ddFilter.fromFilter.controlName]: [ddFilter.fromFilter.initialValue, ddFilter.fromFilter.validators],
                  [ddFilter.toFilter.controlName]: [ddFilter.toFilter.initialValue, ddFilter.toFilter.validators],
                },
                { validators: ddFilter.validators },
              );
              return acc;
            } else {
              acc[filter.controlName] = [filter.initialValue, filter.validators];
              return acc;
            }
          }, {}),
        );
        return acc;
      }, {}),
    );
    this.patchForm();
    this.saveInitialForm();
  }
}
